import { useState, useEffect, useCallback } from 'react'
import { useApi } from '@/hooks/useApi'
import type { HiloGmail } from '@/lib/types'
import { Modal, Button } from '@/components/ui'
import { IconSearch, IconMail, IconCheck } from '@/components/icons/Icons'

/*
  Busca hilos en el buzón de Google conectado y los archiva en la ficha.
  Al abrirse lanza la búsqueda por defecto (los correos de los contactos del
  cliente), para que la primera pantalla ya sirva sin escribir nada.
*/
interface Props {
  basePath: string
  /** Cómo se llama la ficha en los textos: «proyecto», «oportunidad». */
  nombreFicha: string
  open: boolean
  onClose: () => void
  onArchivado: () => void
}

export default function BuscarCorreoModal({ basePath, nombreFicha, open, onClose, onArchivado }: Props) {
  const api = useApi()
  const [query, setQuery] = useState('')
  const [hilos, setHilos] = useState<HiloGmail[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [archivando, setArchivando] = useState<string | null>(null)

  const buscar = useCallback(async (q: string) => {
    setLoading(true)
    setError(null)
    try {
      const qs = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''
      const data = await api.get<{ hilos: HiloGmail[] }>(`${basePath}/buscar${qs}`)
      setHilos(data.hilos || [])
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'No se pudo buscar en Gmail')
      setHilos([])
    } finally {
      setLoading(false)
    }
  }, [basePath]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) { setQuery(''); buscar('') }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  async function archivar(threadId: string) {
    setArchivando(threadId)
    try {
      await api.post(`${basePath}`, { gmail_thread_id: threadId })
      // Se marca en el acto para poder archivar varios seguidos sin cerrar.
      setHilos(prev => prev.map(h => h.threadId === threadId ? { ...h, ya_archivado: true } : h))
      onArchivado()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'No se pudo archivar el correo')
    } finally {
      setArchivando(null)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={`Archivar un correo en esta ${nombreFicha}`} width={820}>
      <form
        onSubmit={(e) => { e.preventDefault(); buscar(query) }}
        style={{ display: 'flex', gap: 8, marginBottom: 16 }}
      >
        <input
          className="form-input"
          style={{ flex: 1 }}
          placeholder="Buscar en Gmail: remitente, asunto o texto…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Button type="submit" disabled={loading}>
          <IconSearch size={14} /> Buscar
        </Button>
      </form>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: 14 }}>
          {error}
          {/* Los dos fallos más probables se arreglan fuera del ERP, así que
              conviene decir dónde en vez de dejar solo el mensaje. */}
          {error.includes('permiso') && (
            <> Andá a <a href="/configuracion">Configuración</a> y volvé a conectar Google.</>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-muted">Buscando en Gmail…</p>
      ) : hilos.length === 0 ? (
        <p className="text-muted">No se encontraron correos. Probá con otra búsqueda.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {hilos.map(h => (
            <div key={h.threadId} className="card" style={{ padding: 14, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <IconMail size={16} style={{ marginTop: 3, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>{h.asunto}</div>
                <div className="text-muted" style={{ fontSize: 13, marginBottom: 4 }}>
                  {h.de}
                  {h.fecha && <> · {new Date(h.fecha).toLocaleDateString('es-PE')}</>}
                  {h.cantidad > 1 && <> · {h.cantidad} mensajes</>}
                </div>
                <div className="text-muted" style={{ fontSize: 13 }}>{h.fragmento}</div>
              </div>
              {h.ya_archivado ? (
                <span className="badge badge-completado" style={{ flexShrink: 0 }}>
                  <IconCheck size={12} /> Archivado
                </span>
              ) : (
                <Button
                  variant="secondary"
                  onClick={() => archivar(h.threadId)}
                  disabled={archivando === h.threadId}
                  style={{ flexShrink: 0 }}
                >
                  {archivando === h.threadId ? 'Archivando…' : 'Archivar'}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  )
}
